import BaseTableNew from 'components/atoms/base-table/new';
import { ACTIVITY_PER_PAGE } from 'infrastructure/hooks/rpm';
import { scrollToTopAsync } from 'infrastructure/functions';
import { type FC, type Dispatch, type SetStateAction, useMemo } from 'react';
import EmptyTablePlaceholder from 'components/atoms/base-empty-table-placeholder';
import { useTranslation } from 'react-i18next';
import SearchIcon from 'components/atoms/icons/search.svg?react';
import classNames from 'classnames';
import { ELASTIC_SEARCH_MAX_ITEMS } from 'constants/count';
import { safeClamp } from 'utils/helpers/safeClamp';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'router/routes';
import useUser from 'utils/useUser';
import { usePatientLatestReading } from 'infrastructure/hooks/rpm/overview-dashboard/use-patient-latest-reading';

import s from './styles.module.scss';
import { useRpmOverviewTableColumns } from './components/columns';

interface Props {
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  patients?: any[];
  pageSize?: number;
  setPerPage?: Dispatch<SetStateAction<number>>;
  showSizeChanger?: boolean;
  wrapperId: string;
  startTime?: string;
  endTime?: string;
}

const RpmOverviewTable: FC<Props> = ({
  patients,
  loading,
  page,
  setPage,
  setPerPage,
  pageSize = ACTIVITY_PER_PAGE,
  showSizeChanger,
  wrapperId,
  startTime,
  endTime,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isCriticalOrAtRiskAlertingRole, isMedicalAssistantRole } = useUser();

  const patientGuids = useMemo(() => {
    if (!patients || patients.length === 0) {
      return [];
    }
    return patients.map((patient) => patient.guid);
  }, [patients]);

  const {
    latestReadings,
    isLoading: isLoadingReadings,
    isFetching: isFetchingReadings,
  } = usePatientLatestReading({
    patientGuids,
    startTime: startTime || '',
    endTime: endTime || '',
    skipMACheck: isMedicalAssistantRole || undefined,
    applySpecialFilter: isCriticalOrAtRiskAlertingRole === true,
    reviewed: false,
  });

  const columns = useRpmOverviewTableColumns(
    isLoadingReadings || isFetchingReadings,
  );

  const patientsWithLatestReadings = useMemo(() => {
    if (!patients || patients.length === 0) {
      return [];
    }

    return patients.map((patient) => {
      const latestReading = latestReadings.find(
        (reading) => reading.patientGuid === patient.guid,
      );

      return {
        ...patient,
        latestReading: latestReading || null,
      };
    });
  }, [patients, latestReadings]);

  return (
    <>
      <BaseTableNew
        dataCy="overview-dashboard-table"
        loading={loading}
        columns={columns}
        items={patientsWithLatestReadings ?? []}
        bordered={false}
        rowKey="guid"
        pagination={{
          current: page,
          pageSize,
          total: safeClamp(
            patientsWithLatestReadings?.length,
            0,
            ELASTIC_SEARCH_MAX_ITEMS,
          ),
          position: ['bottomCenter'],
          showSizeChanger,
        }}
        onChangePage={(newPage, newPageSize) => {
          scrollToTopAsync(wrapperId, 70).then(() => {
            setPage(newPage);
            if (pageSize !== newPageSize) {
              setPage(1);
              setPerPage?.(newPageSize);
            }
          });
        }}
        emptyText={
          <EmptyTablePlaceholder
            text={t('labels.noReadingsFound')}
            icon={<SearchIcon width={100} height={100} />}
            column
          />
        }
        rowClassName={classNames(s['table-row'], {
          [s['table-row--disabled']]: isCriticalOrAtRiskAlertingRole,
        })}
        rowHoverable={false}
        changeOddRowColor={false}
        className={s.table}
        showHeader={false}
        onRow={(record, rowIndex) => ({
          'data-cy': `overview-dashboard-table-row-${rowIndex}`,
          id: undefined,
          onClick: (e) => {
            if (isCriticalOrAtRiskAlertingRole) return;

            const target = e.target as HTMLElement;

            if (target.closest('[data-expanded-content="true"]')) {
              return;
            }

            if (
              target.closest('a') ||
              target.closest('button') ||
              target.closest('[role="button"]') ||
              target.closest('input') ||
              target.closest('select') ||
              target.closest('textarea')
            ) {
              return;
            }

            navigate(Routes.patientGuid(record.guid));
          },
        })}
      />
    </>
  );
};
export default RpmOverviewTable;
